import type {
  BoardSlug,
  DraftStyle,
  PostDetail,
  TalkCategory,
  TradePlace,
  TradeType,
} from '@tomato/shared';
import {
  AI_DRAFT_MAX_USES,
  allowsPrice,
  BOARD_LABELS,
  BOARD_TRADE_TYPES,
  DRAFT_STYLES,
  isMarketBoard,
  POST_LIMITS,
  TALK_CATEGORIES,
  TALK_CATEGORY_LABELS,
  TRADE_PLACE_LABELS,
  TRADE_PLACES,
  TRADE_TYPE_LABELS,
} from '@tomato/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useLeaveConfirm } from '../lib/useLeaveConfirm';

import { api, ApiError } from '../api/client';
import { ImageUploader } from '../components/ImageUploader';
import { Button, Label, Notice, Select, Spinner, TextArea, TextField } from '../components/ui';
import { renderMarkdown } from '../lib/markdown';
import { useSessionStore } from '../store/session';

/** 작성/수정 겸용. :id가 있으면 수정. */
export function PostEditorScreen() {
  const { slug, id } = useParams<{ slug?: BoardSlug; id?: string }>();
  const navigate = useNavigate();
  const refreshMe = useSessionStore((s) => s.refreshMe);
  const me = useSessionStore((s) => s.me);
  const editing = Boolean(id);
  // 관리자만 글쓴이를 고를 수 있다. 새 글에서만(수정은 명의 고정).
  const isAdmin = me?.role === 'admin';

  const [boardSlug, setBoardSlug] = useState<BoardSlug>(slug ?? 'market');
  const [asAdmin, setAsAdmin] = useState(false);
  const [title, setTitle] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [category, setCategory] = useState<TalkCategory | ''>('');
  const [imagePaths, setImagePaths] = useState<string[]>([]);
  const [tradeType, setTradeType] = useState<TradeType>('sell');
  const [price, setPrice] = useState('');
  const [tradePlace, setTradePlace] = useState<TradePlace | ''>('');
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // AI 작성 지원(장터 새 글에서만). me.aiDraft가 true여야 서버 키가 설정된 것.
  const [aiNotes, setAiNotes] = useState('');
  const [aiStyle, setAiStyle] = useState<DraftStyle>('default');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');
  // 이 글 작성 세션에서 AI 초안을 쓴 횟수. 성공한 요청만 센다.
  const [aiUses, setAiUses] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void api
      .get<{ post: PostDetail }>(`/api/posts/${id}`)
      .then((res) => {
        if (cancelled) return;
        setBoardSlug(res.post.boardSlug);
        setTitle(res.post.title);
        setBodyMd(res.post.bodyMd);
        setCategory(res.post.category ?? '');
        // 서버는 프록시 URL로 주므로 저장 경로만 되뽑는다.
        setImagePaths(res.post.images.map((img) => img.url.replace('/api/images/', '')));
        if (res.post.tradeType) setTradeType(res.post.tradeType);
        setPrice(res.post.price === null ? '' : String(res.post.price));
        setTradePlace(res.post.tradePlace ?? '');
      })
      .catch(() => !cancelled && setError('글을 불러오지 못했어요.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const html = useMemo(() => (preview ? renderMarkdown(bodyMd) : ''), [preview, bodyMd]);
  const isMarket = isMarketBoard(boardSlug);
  // 게시판별 거래유형 — 장터는 팝니다·나눔, 책방은 팝니다·교환.
  const tradeTypeOptions = isMarketBoard(boardSlug) ? BOARD_TRADE_TYPES[boardSlug] : [];
  // 나눔은 가격이라는 개념이 없다. 입력 자체를 막는다.
  const priceDisabled = !allowsPrice(tradeType);
  const canSubmit = title.trim().length > 0 && bodyMd.trim().length > 0 && !busy;

  // 작성 중 이탈 경고. 로드된 초기 상태와 달라지면(=변경 있음) 뒤로가기·새로고침 시 확인을 띄운다.
  const initialSnapshotRef = useRef<string | null>(null);
  const snapshot = JSON.stringify({
    boardSlug,
    title,
    bodyMd,
    category,
    tradeType,
    price,
    tradePlace,
    imagePaths,
  });
  useEffect(() => {
    if (loading) return; // 수정 글은 불러오기가 끝난 뒤의 값을 기준으로 삼는다.
    if (initialSnapshotRef.current === null) initialSnapshotRef.current = snapshot;
  }, [loading, snapshot]);
  const dirty = !busy && initialSnapshotRef.current !== null && snapshot !== initialSnapshotRef.current;
  const safeLeave = useLeaveConfirm(dirty, '작성 중인 내용이 저장되지 않고 사라져요. 정말 나가시겠어요?');

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    const payload = {
      boardSlug,
      title: title.trim(),
      bodyMd: bodyMd.trim(),
      category: boardSlug === 'talk' ? category || null : null,
      imagePaths,
      ...(isAdmin && !editing && asAdmin ? { asAdmin: true } : {}),
      ...(isMarket
        ? {
            tradeType,
            price: priceDisabled || price === '' ? null : Number(price),
            tradePlace: tradePlace || null,
          }
        : {}),
    };
    try {
      const res = editing
        ? await api.patch<{ post: PostDetail }>(`/api/posts/${id}`, payload)
        : await api.post<{ post: PostDetail }>('/api/posts', payload);
      // 새 글이면 토마토가 적립됐다 — 잔액을 바로 반영한다.
      if (!editing) void refreshMe();
      // 저장은 의도된 이탈 — 이탈 가드 센티넬을 접고 이동한다(뒤로가기 시 빈 에디터 방지).
      safeLeave(() => navigate(`/posts/${res.post.id}`, { replace: true }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했어요.');
      setBusy(false);
    }
  }

  const aiEnabled = isMarket && !editing && me?.aiDraft === true;

  const aiUsesLeft = AI_DRAFT_MAX_USES - aiUses;

  async function generateDraft() {
    if (aiBusy || aiNotes.trim().length < 2 || aiUsesLeft <= 0) return;
    setAiBusy(true);
    setAiError('');
    try {
      const res = await api.post<{ draft: { title: string; bodyMd: string } }>('/api/ai/draft', {
        notes: aiNotes.trim(),
        tradeType,
        style: aiStyle,
      });
      setTitle(res.draft.title);
      setBodyMd(res.draft.bodyMd);
      setPreview(false);
      setAiUses((n) => n + 1); // 성공만 카운트 — 오류로 실패한 요청은 차감하지 않는다.
    } catch (err) {
      setAiError(err instanceof ApiError ? err.message : 'AI 초안을 만들지 못했어요.');
    } finally {
      setAiBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="py-24 text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-[22px] font-semibold tracking-tight text-ink-900">
          {editing ? '글 수정' : `${BOARD_LABELS[boardSlug]}에 글쓰기`}
        </h1>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          취소
        </Button>
      </div>

      {isAdmin && !editing && (
        <label className="mb-5 flex items-center gap-2.5 text-[14px] text-ink-700">
          <input
            type="checkbox"
            checked={asAdmin}
            disabled={busy}
            onChange={(e) => setAsAdmin(e.target.checked)}
            className="h-4 w-4 rounded border-ink-300 text-tomato-600 focus:ring-tomato-500"
          />
          <span>
            <b className="font-medium">농장 관리인</b> 명의로 올리기{' '}
            <span className="text-ink-400">(관리자 전용)</span>
          </span>
        </label>
      )}

      {isMarket && (
        <div className="mb-5 grid grid-cols-2 gap-3">
          <div>
            <Label>거래 유형</Label>
            <Select
              value={tradeType}
              disabled={busy}
              onChange={(e) => setTradeType(e.target.value as TradeType)}
            >
              {tradeTypeOptions.map((code) => (
                <option key={code} value={code}>
                  {TRADE_TYPE_LABELS[code]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label hint={priceDisabled ? '해당 없음' : '선택'}>가격</Label>
            <TextField
              inputMode="numeric"
              value={priceDisabled ? '' : price}
              disabled={busy || priceDisabled}
              placeholder={
                tradeType === 'free' ? '무료' : priceDisabled ? '—' : '찜한 사람과 대화로 협의할 수 있어요'
              }
              onChange={(e) => setPrice(e.target.value.replace(/\D/g, '').slice(0, 9))}
            />
          </div>
        </div>
      )}

      {isMarket && (
        <div className="mb-5">
          <Label hint="선택">거래 장소</Label>
          <Select
            value={tradePlace}
            disabled={busy}
            onChange={(e) => setTradePlace(e.target.value as TradePlace | '')}
          >
            <option value="">정하지 않음</option>
            {TRADE_PLACES.map((code) => (
              <option key={code} value={code}>
                {TRADE_PLACE_LABELS[code]}
              </option>
            ))}
          </Select>
        </div>
      )}

      {boardSlug === 'talk' && (
        <div className="mb-5">
          <Label hint="선택">카테고리</Label>
          <Select
            value={category}
            disabled={busy}
            onChange={(e) => setCategory(e.target.value as TalkCategory | '')}
          >
            <option value="">선택 안 함</option>
            {TALK_CATEGORIES.map((code) => (
              <option key={code} value={code}>
                {TALK_CATEGORY_LABELS[code]}
              </option>
            ))}
          </Select>
        </div>
      )}

      <div className="mb-5">
        <Label hint={`${title.length}/${POST_LIMITS.titleMax}`}>제목</Label>
        <TextField
          value={title}
          maxLength={POST_LIMITS.titleMax}
          disabled={busy}
          autoFocus={!editing}
          placeholder={
            boardSlug !== 'talk'
              ? '무엇을 나눌까요?'
              : category === 'groupbuy'
                ? '같이 살 물건을 추천해 주세요 (예: 코스트코 대용량)'
                : '무엇을 말하고 싶으신가요?'
          }
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="mb-5">
        <Label hint={`최대 ${POST_LIMITS.maxImages}장`}>사진</Label>
        <ImageUploader paths={imagePaths} onChange={setImagePaths} disabled={busy} />
      </div>

      {aiEnabled && (
        <div className="mb-5 rounded-xl bg-ink-50 p-3.5">
          <p className="text-[13px] font-medium text-ink-700">✨ AI 작성 지원</p>
          <p className="mt-0.5 mb-2 text-[12px] leading-relaxed text-ink-400">
            대충 적어도 괜찮아요. 예: “의자, 한달 12만원, 태광 1층, 네고 안됨”. 초안을 만들어 주면
            아래에서 직접 고칠 수 있어요.
          </p>
          <TextArea
            rows={2}
            value={aiNotes}
            maxLength={500}
            disabled={aiBusy || busy}
            placeholder="팔 물건·가격·장소·조건을 생각나는 대로 적어주세요."
            onChange={(e) => setAiNotes(e.target.value)}
          />
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[12px] text-ink-400">말투</span>
            {DRAFT_STYLES.map((s) => {
              const on = aiStyle === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  disabled={aiBusy || busy}
                  onClick={() => setAiStyle(s.value)}
                  className={`rounded-full px-2.5 py-1 text-[12px] transition ${
                    on
                      ? 'bg-tomato-600 font-medium text-white'
                      : 'bg-surface text-ink-500 hairline hover:text-ink-700'
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          <div className="mt-2.5 flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={aiBusy || busy || aiNotes.trim().length < 2 || aiUsesLeft <= 0}
              onClick={() => void generateDraft()}
            >
              {aiBusy ? '만드는 중…' : 'AI로 초안 만들기'}
            </Button>
            {aiError ? (
              <span className="text-[12px] text-tomato-600">{aiError}</span>
            ) : aiUsesLeft <= 0 ? (
              <span className="text-[12px] text-ink-400">이 글에서는 다 썼어요 (5회)</span>
            ) : (
              <span className="text-[12px] text-ink-400">이 글에서 {aiUsesLeft}회 남음</span>
            )}
          </div>
        </div>
      )}

      <div className="mb-5">
        <Label
          hint={
            <button
              onClick={() => setPreview((p) => !p)}
              className="text-tomato-600 transition hover:text-tomato-700"
            >
              {preview ? '계속 쓰기' : '미리보기'}
            </button>
          }
        >
          내용
        </Label>

        {preview ? (
          <div className="min-h-[280px] rounded-xl bg-surface p-5 shadow-card">
            {bodyMd.trim() ? (
              <div className="prose-tomato" dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              <p className="text-[13px] text-ink-300">내용을 입력하면 여기에 보여요.</p>
            )}
          </div>
        ) : (
          <TextArea
            rows={14}
            value={bodyMd}
            maxLength={POST_LIMITS.bodyMax}
            disabled={busy}
            placeholder={'자유롭게 적어주세요.\n\n**굵게**, *기울임*, - 목록 같은 마크다운을 쓸 수 있어요.'}
            onChange={(e) => setBodyMd(e.target.value)}
          />
        )}
      </div>

      <Button className="w-full" disabled={!canSubmit} onClick={() => void submit()}>
        {busy ? '저장 중…' : editing ? '수정하기' : '올리기'}
      </Button>
      <Notice>{error}</Notice>
    </div>
  );
}
